import { Grid } from '@mui/material';
import { Workspace } from 'types/Workspace';
import SettingsChallengesForm, {
  SettingsChallengesFormValues,
} from './WorkspaceSettingsForms/EditMainChallengesForm';
import SettingsObjectiveForm, {
  SettingsObjectiveFormValues,
} from './WorkspaceSettingsForms/EditKeyObjectiveForm';
import { useCompanyOverviewByID } from 'hooks/CompanyOverview/useCompanyOverviewByID';
import EditCompanyOverviewForm from './WorkspaceSettingsForms/EditCompanyOverviewForm';
import {
  useBatchAddKeyObjectives,
  useBatchAddMainChallenges,
  useBatchRemoveKeyObjectives,
  useBatchRemoveMainChallenges,
} from 'hooks/batchOperations';
import { useUpdateCompanyOverview } from 'hooks/CompanyOverview/useUpdateCompanyOverview';
import { SPARK_RESOURCE_TYPES } from 'constants/common';

type EditCompanyInfoFromSettingsProps = {
  companyOverviewID: string | undefined;
  workspace: Workspace | undefined;
};

function WorkspaceSettingsTab({
  workspace,
  companyOverviewID,
}: EditCompanyInfoFromSettingsProps) {
  const { mutateAsync: addKeyObjectives } = useBatchAddKeyObjectives(
    SPARK_RESOURCE_TYPES.COMPANY_OVERVIEW_KEY_OBJECTIVES,
    companyOverviewID
  );

  const { mutateAsync: removeKeyObjectives } = useBatchRemoveKeyObjectives(
    SPARK_RESOURCE_TYPES.COMPANY_OVERVIEW_KEY_OBJECTIVES,
    companyOverviewID
  );
  const { mutateAsync: updateCompanyOverview } =
    useUpdateCompanyOverview(companyOverviewID);

  const { mutateAsync: addMainChallenges } = useBatchAddMainChallenges(
    SPARK_RESOURCE_TYPES.COMPANY_OVERVIEW_MAIN_CHALLENGES,
    companyOverviewID
  );
  const { mutateAsync: removeMainChallenges } = useBatchRemoveMainChallenges(
    SPARK_RESOURCE_TYPES.COMPANY_OVERVIEW_MAIN_CHALLENGES,
    companyOverviewID
  );

  const {
    companyOverview,
    marketApproach,
    keyObjectives,
    companyOverviewKeyObjectives,
    mainChallenges,
    companyOverviewMainChallenges,
    companyOverviewMarketApproach,
  } = useCompanyOverviewByID(companyOverviewID);

  const handleSubmitSettingsObjectiveForm = async (
    values: SettingsObjectiveFormValues
  ) => {
    if (companyOverviewKeyObjectives?.length) {
      await removeKeyObjectives({
        keyObjectiveIDs: companyOverviewKeyObjectives.map(
          (companyOverviewKeyObjective) => companyOverviewKeyObjective.id
        ),
      });
    }

    updateCompanyOverview({ otherKeyObjectives: values.otherObjective });
    addKeyObjectives({ keyObjectivesIDs: values.objectiveIDs });
  };

  const handleSubmitSettingsChallengesForm = async (
    values: SettingsChallengesFormValues
  ) => {
    const oldCompanyOverviewMainChallengesIDs =
      companyOverviewMainChallenges?.map((challenge) => challenge.id) || [];
    if (oldCompanyOverviewMainChallengesIDs.length) {
      await removeMainChallenges({
        mainChallengesIDs: oldCompanyOverviewMainChallengesIDs,
      });
    }
    updateCompanyOverview({ otherMainChallenges: values.otherChallenge });
    addMainChallenges({ mainChallengeIDs: values.challengesIDs });
  };

  return (
    <Grid container item alignItems='center'>
      <EditCompanyOverviewForm
        workspace={workspace}
        marketApproaches={marketApproach}
        companyOverview={companyOverview}
        companyOverviewMarketApproach={companyOverviewMarketApproach}
      />

      <SettingsObjectiveForm
        keyObjectives={keyObjectives}
        otherObjective={companyOverview?.attributes?.otherKeyObjectives}
        categoryID={workspace?.attributes?.categoryID}
        onSubmit={handleSubmitSettingsObjectiveForm}
      />
      <SettingsChallengesForm
        mainChallenges={mainChallenges}
        categoryID={workspace?.attributes?.categoryID}
        otherChallenge={companyOverview?.attributes?.otherKeyObjectives}
        onSubmit={handleSubmitSettingsChallengesForm}
      />
    </Grid>
  );
}

export default WorkspaceSettingsTab;
